import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import FormGroup from '../../../components/global/FormGroup';
import Button from '../../../components/global/Button';
import withModal from '../../../components/global/modal/withModal';
import { CREATE_ACCOUNT_IDENTITY_MODAL } from '../constants/modals';
import ContextualString from '../../../components/contextual/ContextualString';
import {
  getIdentityNameHeuristic,
  isPrimaryIdentity,
} from '../../../util/identity';
import Alert from '../../../components/global/Alert';
import withVirtualPageview from '../../../components/global/hoc/withVirtualPageview';
import Label from '../../../components/global/Label';
import IdentityGenderSelectionButtons from './IdentityGenderSelectionButtons';
import { requiresGenderForOnboarding } from '../../../util/album-folder';
import { getAsset } from '../../../util/assets';
import useOpenModal from '../../../hooks/useOpenModal';
import UpdateSelfieFormModal, {
  UPDATE_SELFIE_FORM_MODAL,
} from '../../../components/global/UpdateSelfieForm/modal';
import { AVATAR_SIZE_72 } from '../../../constants/avatar-sizes';
import IdentityAvatar from '../../../components/global/identity/IdentityAvatar';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  identityCreate: PropTypes.func.isRequired,
  identityCreateReset: PropTypes.func.isRequired,
  identityNames: PropTypes.arrayOf(PropTypes.string),
  // eslint-disable-next-line react/no-unused-prop-types
  success: PropTypes.bool,
  onRequestClose: PropTypes.func,
  onIdentityCreated: PropTypes.func,
  stringContext: PropTypes.shape({
    album_type: PropTypes.string,
    album_subtype: PropTypes.string,
  }),
  submitLabel: PropTypes.string,
};

const CreateAccountIdentity = ({
  account,
  identityCreate,
  identityCreateReset,
  identityNames = [],
  error,
  loading,
  success,
  onRequestClose,
  onIdentityCreated,
  stringContext,
  includeTitle = true,
  submitLabel = 'Create',
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fileUrl, setFileUrl] = useState(getAsset('/icon-add-face-blue.svg'));
  const [file, setFile] = useState(null);
  const [gender, setGender] = useState(null);
  const [localError, setLocalError] = useState('');
  const [localErrorFields, setLocalErrorFields] = useState([]);
  const openModal = useOpenModal();

  const generateErrorList = () => {
    const errorList = [];
    if (!firstName) {
      errorList.push('First Name');
    }
    if (!lastName) {
      errorList.push('Last Name');
    }
    if (!file) {
      errorList.push('Upload Photo');
    }

    return errorList;
  };

  useEffect(() => {
    identityCreateReset();
    const primaryIdentity = (account?.linked_identities?.records ?? []).find(
      isPrimaryIdentity,
    );

    setLastName(primaryIdentity?.last_name ?? '');
  }, []);

  useEffect(() => {
    if (success) {
      onRequestClose?.();
      // Reset the success value before closing the modal. Otherwise,
      // this effect will run with SUCCESS when this modal is opened again.
      identityCreateReset();
    }
  }, [success]);

  useEffect(() => {
    const errorList = generateErrorList();

    setLocalErrorFields(errorList);
    if (errorList.length === 0) {
      setLocalError('');
    }
  }, [firstName, lastName, file]);

  const isValidName = () => {
    const name = getIdentityNameHeuristic({
      name: firstName,
      last_name: lastName,
    });

    return !identityNames.includes(name);
  };

  const onCreateIdentity = async event => {
    if (event) {
      event.preventDefault();
    }
    setLocalError('');
    setLocalErrorFields([]);

    if (!firstName || !lastName || !file) {
      setLocalError(
        "Don't forget to fill all required fields. Please provide:",
      );
      const errorList = generateErrorList();
      setLocalErrorFields(errorList);
      return;
    }

    if (!isValidName()) {
      setLocalError(
        'You already have someone with this name. Please add only one face per person.',
      );
      return;
    }

    const identity = await identityCreate({
      accountId: account.id,
      identityFirstName: firstName,
      identityLastName: lastName,
      gender,
      file,
    });

    if (onIdentityCreated && identity) {
      onIdentityCreated(identity);
    }
  };

  useEffect(() => {
    if (file && firstName && lastName) {
      onCreateIdentity();
    }
  }, [file]);

  const requiresGender = requiresGenderForOnboarding(stringContext);

  return (
    <form method="post" onSubmit={onCreateIdentity} className="px-md-4">
      {includeTitle && (
        <h3 className="text-center mb-2">
          Add new{' '}
          <ContextualString
            id="identityReference"
            defaultMessage="face"
            type={get(stringContext, 'album_type')}
            subtype={get(stringContext, 'album_subtype')}
          />
        </h3>
      )}

      <FormGroup
        id="identity-first-name"
        name="identityFirstName"
        type="text"
        label="First Name"
        labelType="expanded"
        required
        autoFocus
        value={firstName}
        onChange={({ target }) => setFirstName(target.value)}
      />

      <FormGroup
        id="identity-last-name"
        name="identityLastName"
        type="text"
        label="Last Name"
        labelType="expanded"
        required
        value={lastName}
        onChange={({ target }) => setLastName(target.value)}
      />

      {requiresGender && (
        <Fragment>
          <Label id="identity-gender" labelNote="(required)" label="Gender" />
          <IdentityGenderSelectionButtons
            value={gender}
            onChange={value => setGender(value)}
            className="mb-2"
          />
        </Fragment>
      )}

      <div className="d-flex justify-content-center align-items-center mb-4">
        <IdentityAvatar
          uploadedPhoto={{
            url: fileUrl,
          }}
          size={AVATAR_SIZE_72}
          onClick={event => event.preventDefault()}
          className="d-flex flex-column justify-content-center align-items-center identity-avatar-icon"
        />
        <Button
          btnBlock
          btnOutline={!!file}
          className="ml-3"
          onClick={event => {
            event.preventDefault();
            openModal(UPDATE_SELFIE_FORM_MODAL);
          }}
        >
          {file ? 'Update Photo' : 'Add Photo'}
        </Button>
      </div>

      {localError && (
        <Alert type="danger">
          {localError}
          {localErrorFields.length > 0 && (
            <Fragment>
              <ul className="mb-0">
                {localErrorFields.map(err => (
                  <li>{err}</li>
                ))}
              </ul>
            </Fragment>
          )}
        </Alert>
      )}
      {error && <Alert type="danger">{error}</Alert>}

      <Button
        btnBlock
        className="my-3"
        type="submit"
        disabled={
          loading || !firstName || !lastName || (requiresGender && !gender)
        }
      >
        {loading ? 'Submitting...' : submitLabel}
      </Button>
      <UpdateSelfieFormModal
        uploadOnly
        user={account}
        onSuccess={uploadedFile => {
          setFile(uploadedFile);
          setFileUrl(uploadedFile.url);
        }}
      />
    </form>
  );
};

CreateAccountIdentity.propTypes = propTypes;

export { CreateAccountIdentity as CreateAccountIdentityComponent };

export default withModal(
  withVirtualPageview({
    path: 'new-identity',
    title: 'New Identity',
  })(CreateAccountIdentity),
  {
    name: CREATE_ACCOUNT_IDENTITY_MODAL,
    size: 'sm',
    type: 'full',
  },
);
